import { Plus } from 'lucide-react';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import {
  type CreateSsoConnectionRequest,
  type SsoConnection,
  type SsoDomain,
  type UpdateSsoConnectionRequest,
} from '@column/api';
import { Button, Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@column/ui';

import { RenderFields, Section } from '~/components';

import { SSO_MODES } from './constants';
import { DomainsTable } from './DomainsTable';
import { ServiceProviderDetails } from './ServiceProviderDetails';

export type SSOConnectionFields = CreateSsoConnectionRequest & UpdateSsoConnectionRequest;

interface ConnectionFormSectionsProps {
  connection?: SsoConnection;
  onAddDomain: () => void;
  onSelectDomain: (domainId: string) => void;
  onRemoveDomain: (domain: SsoDomain) => void;
}

export const ConnectionFormSections: React.FC<ConnectionFormSectionsProps> = ({
  connection,
  onAddDomain,
  onSelectDomain,
  onRemoveDomain,
}) => {
  const {
    formState: { errors, isSubmitting },
  } = useFormContext<SSOConnectionFields>();

  return (
    <>
      <div className="bg-gray-25 dark:bg-white/[0.02]">
        <Section headline="Configuration parameters" isCollapsed={false}>
          <ServiceProviderDetails connection={connection} />
        </Section>
      </div>
      <div className="relative z-1 bg-background shadow-[0_-1px_0_var(--color-border)]">
        {connection &&
          RenderFields<SSOConnectionFields>({
            sections: [
              {
                headline: 'Login',
                isCollapsed: false,
                fields: [
                  {
                    id: 'mode',
                    label: 'Mode',
                    description: 'Configure when single sign-on is used.',
                    children: ({ value, onChange }) => (
                      <Select value={String(value)} onValueChange={onChange}>
                        <SelectTrigger aria-label="Mode" className="w-full">
                          <SelectValue>{SSO_MODES.find((option) => option.value === value)?.label}</SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {SSO_MODES.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              <span className="flex flex-col items-start gap-0.5">
                                <span>{option.label}</span>
                                <span className="text-xs font-normal text-muted-foreground">{option.description}</span>
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ),
                  },
                ],
              },
            ],
          })}
        {RenderFields<SSOConnectionFields>({
          sections: [
            {
              headline: 'Identity provider',
              isCollapsed: false,
              fields: [
                {
                  id: 'display_name',
                  label: 'Display name',
                  rules: { required: true },
                  children: ({ value, onChange, onBlur, ref }, { error }) => (
                    <Input
                      ref={ref}
                      placeholder="Display name"
                      aria-invalid={!!error}
                      disabled={isSubmitting}
                      value={String(value ?? '')}
                      onChange={onChange}
                      onBlur={onBlur}
                    />
                  ),
                },
                {
                  id: 'issuer',
                  label: 'Issuer URL',
                  description: 'OIDC issuer URL from your identity provider.',
                  validationMode: 'onBlur',
                  error: errors.issuer?.message ? { title: errors.issuer.message } : undefined,
                  rules: {
                    required: true,
                    pattern: { value: /^https:\/\/\S+$/, message: 'Issuer URL must use https.' },
                  },
                  children: ({ value, onChange, onBlur, ref }, { error }) => (
                    <Input
                      ref={ref}
                      placeholder="https://login.example.com"
                      aria-invalid={!!error}
                      disabled={isSubmitting}
                      value={String(value ?? '')}
                      onChange={onChange}
                      onBlur={onBlur}
                    />
                  ),
                },
                {
                  id: 'client_id',
                  label: 'Client ID',
                  description: 'Client ID for Column in your identity provider.',
                  rules: { required: true },
                  children: ({ value, onChange, onBlur, ref }, { error }) => (
                    <Input
                      ref={ref}
                      placeholder="Client ID"
                      className="font-mono"
                      aria-invalid={!!error}
                      disabled={isSubmitting}
                      value={String(value ?? '')}
                      onChange={onChange}
                      onBlur={onBlur}
                    />
                  ),
                },
                {
                  id: 'client_secret',
                  label: 'Client secret',
                  description: 'Client secret for Column in your identity provider.',
                  rules: { required: !connection },
                  children: ({ value, onChange, onBlur, ref }, { error }) => (
                    <Input
                      ref={ref}
                      type="password"
                      autoComplete="new-password"
                      placeholder={connection?.has_client_secret ? '••••••••••••' : 'Client secret'}
                      className="font-mono"
                      aria-invalid={!!error}
                      disabled={isSubmitting}
                      value={String(value ?? '')}
                      onChange={onChange}
                      onBlur={onBlur}
                    />
                  ),
                },
              ],
            },
          ],
        })}
        {connection && (
          <Section
            headline="Domains"
            isCollapsed={false}
            notifications={
              <Button
                type="button"
                size="xs"
                variant="secondary"
                className="-my-0.5 self-center"
                onClick={() => onAddDomain()}
              >
                <Plus />
                Add domain
              </Button>
            }
          >
            <DomainsTable domains={connection.domains} onSelect={onSelectDomain} onRemove={onRemoveDomain} />
          </Section>
        )}
      </div>
    </>
  );
};
